import { z } from "zod";

/** Champ facultatif : la chaîne vide d'un formulaire vaut « non renseigné ». */
const optional = z
  .string()
  .trim()
  .min(1)
  .nullish()
  .or(z.literal("").transform(() => null));

export const saveClientInput = z
  .object({
    clientId: z.string().uuid().optional(),
    confirmationId: z.string().optional(),

    type: z.enum(["particulier", "professionnel", "syndic", "collectivite"]),
    firstName: optional,
    lastName: optional,
    companyName: optional,

    siret: z
      .string()
      .regex(/^\d{14}$/, "Le SIRET doit faire 14 chiffres")
      .nullish()
      .or(z.literal("").transform(() => null)),

    email: z
      .string()
      .email("Adresse e-mail invalide")
      .nullish()
      .or(z.literal("").transform(() => null)),

    phone: optional,
    phoneSecondary: optional,
    addressLine1: optional,
    postalCode: z
      .string()
      .regex(/^\d{5}$/, "Le code postal doit faire 5 chiffres")
      .nullish()
      .or(z.literal("").transform(() => null)),
    city: optional,
    notes: optional,

    isPublicEntity: z.boolean().default(false),
    chorusProStructureId: optional,
  })
  .superRefine((data, ctx) => {
    // On désigne un client par ce qui l'identifie réellement : un particulier
    // par son nom, une organisation par sa raison sociale. Sans ça, un devis
    // part avec « Sans nom » en en-tête.
    if (data.type === "particulier" && !data.lastName) {
      ctx.addIssue({ code: "custom", path: ["lastName"], message: "Le nom est requis" });
    }
    if (data.type !== "particulier" && !data.companyName) {
      ctx.addIssue({
        code: "custom",
        path: ["companyName"],
        message: "La raison sociale est requise",
      });
    }
    // Une collectivité relève de la facturation publique : le marquer ici
    // conditionne tout le parcours Chorus Pro en aval.
    if (data.type === "collectivite" && !data.isPublicEntity) {
      ctx.addIssue({
        code: "custom",
        path: ["isPublicEntity"],
        message: "Une collectivité est une entité publique",
      });
    }
  });

export type SaveClientInput = z.infer<typeof saveClientInput>;

export const listClientsInput = z.object({
  confirmationId: z.string().optional(),
  search: z.string().optional(),
  type: z.enum(["particulier", "professionnel", "syndic", "collectivite"]).optional(),
});

export type ListClientsInput = z.infer<typeof listClientsInput>;

export const readClientInput = z.object({
  confirmationId: z.string().optional(),
  clientId: z.string().uuid(),
});

export type ReadClientInput = z.infer<typeof readClientInput>;

export const archiveClientInput = z.object({
  confirmationId: z.string().optional(),
  clientId: z.string().uuid(),
});

export type ArchiveClientInput = z.infer<typeof archiveClientInput>;
